import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import type {
  DangerSignView,
  MaternitySettingsView,
  VaccinesView,
  VaccineView,
} from '@wodi/shared';
import {
  Button,
  Cell,
  Checkbox,
  Column,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import {
  createVaccine,
  maternityKeys,
  maternitySettingsQuery,
  saveImmunisationSchedule,
  saveMaternitySettings,
  updateVaccine,
  vaccinesQuery,
} from '../api/maternity.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { SettingsPage } from '../routes/settings-nav.tsx';
import { toasts } from '../toasts.ts';
import { errorText, readDecimal, readWhole } from './common.tsx';
import styles from './maternity.module.css';

// Settings > Maternity and MCH (docs/plans/phase2-maternity-mch.md §2.9). Every list, line and
// schedule here is the clinical lead's; nothing is shipped and nothing is suggested
// (TODO(clinical-verify) M6, M10, M16). Empty means "not set up" on the clinical screens.

const NONE = 'none';

/** "code: words" per line, as typed, to the list; null when a line doesn't read. */
export function parseSignLines(text: string): DangerSignView[] | null {
  const lines = text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l !== '');
  const out: DangerSignView[] = [];
  for (const line of lines) {
    const at = line.indexOf(':');
    if (at <= 0) return null;
    const code = line.slice(0, at).trim();
    const display = line.slice(at + 1).trim();
    if (code === '' || display === '') return null;
    out.push({ code, display });
  }
  return out;
}

export function signLines(list: readonly DangerSignView[]): string {
  return list.map((s) => `${s.code}: ${s.display}`).join('\n');
}

function SettingsForm({ settings }: { settings: MaternitySettingsView }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [anc, setAnc] = useState(signLines(settings.ancDangerSigns));
  const [mother, setMother] = useState(signLines(settings.postnatalMotherDangerSigns));
  const [baby, setBaby] = useState(signLines(settings.postnatalBabyDangerSigns));
  const num = (n: number | null) => (n === null ? '' : String(n));
  const [start, setStart] = useState(num(settings.partograph.alertStartCm));
  const [rate, setRate] = useState(num(settings.partograph.alertRateCmPerHour));
  const [offset, setOffset] = useState(num(settings.partograph.actionOffsetHours));
  const [store, setStore] = useState(settings.immunisationStore?.id ?? NONE);
  const [cueWeeks, setCueWeeks] = useState(num(settings.pastEddCueWeeks));
  const cue = readWhole(cueWeeks);
  const cueInvalid = cue === 'invalid' || (cue !== null && (cue < 1 || cue > 52));
  const lists = {
    anc: parseSignLines(anc),
    mother: parseSignLines(mother),
    baby: parseSignLines(baby),
  };
  const line = { start: readDecimal(start), rate: readDecimal(rate), offset: readDecimal(offset) };
  const present = Object.values(line).filter((v) => v !== null).length;
  const lineInvalid =
    Object.values(line).some((v) => v === 'invalid') || (present !== 0 && present !== 3);
  const invalid = lineInvalid || cueInvalid || Object.values(lists).some((l) => l === null);
  const n = (v: number | null | 'invalid') => (v === 'invalid' ? null : v);
  const save = useMutation({
    mutationFn: () =>
      saveMaternitySettings({
        ancDangerSigns: lists.anc ?? [],
        postnatalMotherDangerSigns: lists.mother ?? [],
        postnatalBabyDangerSigns: lists.baby ?? [],
        partograph: {
          alertStartCm: n(line.start),
          alertRateCmPerHour: n(line.rate),
          actionOffsetHours: n(line.offset),
        },
        immunisationStoreId: store === NONE ? null : store,
        pastEddCueWeeks: cue === 'invalid' ? null : cue,
        version: settings.version,
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(maternityKeys.settings, next);
      toasts.show({ title: 'Maternity settings saved' });
    },
  });
  const listError = (l: DangerSignView[] | null) =>
    l === null ? { errorMessage: 'Write each sign on its own line as code: words.' } : {};
  const lineNumber = (v: number | null | 'invalid') =>
    v === 'invalid' ? { errorMessage: 'A number, for example 4 or 1.5.' } : {};
  return (
    <form
      className={styles.form}
      aria-label="Maternity settings"
      onSubmit={(event) => {
        event.preventDefault();
        if (!invalid) save.mutate();
      }}
    >
      <TextArea
        label="ANC danger signs"
        value={anc}
        onChange={setAnc}
        rows={4}
        description="One per line, written as code: words. Empty: not set up."
        {...listError(lists.anc)}
      />
      <TextArea
        label="Postnatal danger signs: mother"
        value={mother}
        onChange={setMother}
        rows={4}
        description="One per line, written as code: words. Empty: not set up."
        {...listError(lists.mother)}
      />
      <TextArea
        label="Postnatal danger signs: baby"
        value={baby}
        onChange={setBaby}
        rows={4}
        description="One per line, written as code: words. Empty: not set up."
        {...listError(lists.baby)}
      />
      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>Partograph alert and action lines</legend>
        <p className={styles.meta}>
          All three, or none. With none, the partograph shows the entries and says the lines are not
          set up.
        </p>
        <div className={styles.when}>
          <TextField
            label="Alert line starts at"
            unit="cm"
            width="sm"
            inputMode="decimal"
            value={start}
            onChange={setStart}
            {...lineNumber(line.start)}
          />
          <TextField
            label="Alert line rises"
            unit="cm/h"
            width="sm"
            inputMode="decimal"
            value={rate}
            onChange={setRate}
            {...lineNumber(line.rate)}
          />
          <TextField
            label="Action line follows by"
            unit="h"
            width="sm"
            inputMode="decimal"
            value={offset}
            onChange={setOffset}
            {...lineNumber(line.offset)}
          />
        </div>
        {lineInvalid && present !== 0 && present !== 3 ? (
          <Notice tone="info">Set all three numbers, or clear them all.</Notice>
        ) : null}
      </fieldset>
      <Select
        label="Immunisation store"
        width="md"
        description="Doses given here are taken from this store's stock."
        options={[
          { id: NONE, label: 'Not set up' },
          ...settings.stores.map((s) => ({ id: s.id, label: s.name })),
        ]}
        value={store}
        onChange={(v) => {
          setStore(String(v));
        }}
      />
      <TextField
        label="Past-EDD reminder after"
        unit="weeks"
        width="sm"
        inputMode="numeric"
        value={cueWeeks}
        onChange={setCueWeeks}
        description="The banner says Past EDD, check pregnancy status this many weeks after the EDD. Empty: no reminder. For the clinical lead to set."
        {...(cueInvalid ? { errorMessage: 'A whole number of weeks, 1 to 52.' } : {})}
      />
      {save.error === null ? null : (
        <Notice tone="error">{errorText(save.error, 'Nothing was saved. Try again.')}</Notice>
      )}
      <div className={styles.actions}>
        <Button
          type="submit"
          variant="primary"
          isDisabled={!online || invalid}
          isPending={save.isPending}
          pendingLabel="Saving…"
        >
          Save maternity settings
        </Button>
      </div>
    </form>
  );
}

function VaccineRow({
  vaccine,
  onSaved,
}: {
  vaccine: VaccineView;
  onSaved: (v: VaccinesView) => void;
}) {
  const online = useOnline();
  const toggle = useMutation({
    mutationFn: () =>
      updateVaccine(vaccine.id, {
        serviceItemId: vaccine.serviceItemId,
        code: vaccine.code,
        codeSystem: vaccine.codeSystem,
        codeSystemVersion: vaccine.codeSystemVersion,
        display: vaccine.display,
        charged: vaccine.charged,
        status: vaccine.status === 'active' ? 'inactive' : 'active',
      }),
    onSuccess: onSaved,
  });
  return (
    <Button
      variant="quiet"
      dense
      isDisabled={!online}
      isPending={toggle.isPending}
      pendingLabel="Saving…"
      onPress={() => {
        toggle.mutate();
      }}
    >
      {vaccine.status === 'active' ? 'Switch off' : 'Switch on'}
    </Button>
  );
}

function Vaccines({ view }: { view: VaccinesView }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const onSaved = (next: VaccinesView) => {
    queryClient.setQueryData(maternityKeys.vaccines, next);
    toasts.show({ title: 'Vaccine saved' });
  };
  const [item, setItem] = useState(view.items[0]?.id ?? '');
  const [code, setCode] = useState('');
  const [system, setSystem] = useState('');
  const [systemVersion, setSystemVersion] = useState('');
  const [display, setDisplay] = useState('');
  const [charged, setCharged] = useState(false);
  const add = useMutation({
    mutationFn: () =>
      createVaccine({
        serviceItemId: item,
        code: code.trim(),
        codeSystem: system.trim(),
        codeSystemVersion: systemVersion.trim() === '' ? null : systemVersion.trim(),
        display: display.trim(),
        charged,
        status: 'active',
      }),
    onSuccess: (next) => {
      onSaved(next);
      setCode('');
      setDisplay('');
      setCharged(false);
    },
  });
  const ready = item !== '' && code.trim() !== '' && system.trim() !== '' && display.trim() !== '';
  return (
    <>
      <Table flat dense aria-label="Vaccines">
        <TableHeader>
          <Column isRowHeader>Vaccine</Column>
          <Column>Code</Column>
          <Column>Stock item</Column>
          <Column>Charged</Column>
          <Column>State</Column>
        </TableHeader>
        <TableBody items={view.vaccines} renderEmptyState={() => 'No vaccines set up.'}>
          {(v) => (
            <Row id={v.id}>
              <Cell>{v.display}</Cell>
              <Cell>
                <span className="wodi-id">{v.code}</span>
                <span className={styles.meta}>
                  {` ${v.codeSystem}${v.codeSystemVersion === null ? '' : `, version ${v.codeSystemVersion}`}`}
                </span>
              </Cell>
              <Cell>{v.itemName}</Cell>
              <Cell>{v.charged ? 'Charged' : 'Not charged'}</Cell>
              <Cell>
                <Tag state={v.status === 'active' ? 'active' : 'neutral'}>
                  {v.status === 'active' ? 'In use' : 'Off'}
                </Tag>{' '}
                <VaccineRow vaccine={v} onSaved={onSaved} />
              </Cell>
            </Row>
          )}
        </TableBody>
      </Table>
      {view.items.length === 0 ? (
        <p className={styles.meta}>
          No medicine stock items in the catalogue to set up as vaccines.
        </p>
      ) : (
        <form
          className={styles.form}
          aria-label="Add vaccine"
          onSubmit={(event) => {
            event.preventDefault();
            if (ready) add.mutate();
          }}
        >
          <div className={styles.when}>
            <Select
              label="Stock item"
              width="md"
              options={view.items.map((i) => ({ id: i.id, label: `${i.name} (${i.code})` }))}
              value={item}
              onChange={(v) => {
                setItem(String(v));
              }}
            />
            <TextField label="Vaccine name" width="md" value={display} onChange={setDisplay} />
            <TextField label="Code" width="sm" value={code} onChange={setCode} />
            <TextField
              label="Code system"
              width="md"
              value={system}
              onChange={setSystem}
              description="The system the code comes from, as your reporting uses it."
            />
            <TextField
              label="Code system version"
              width="sm"
              value={systemVersion}
              onChange={setSystemVersion}
              description="If known. Copied onto each dose."
            />
          </div>
          <Checkbox isSelected={charged} onChange={setCharged}>
            Charge for a dose given here
          </Checkbox>
          {add.error === null ? null : (
            <Notice tone="error">
              {errorText(add.error, 'The vaccine was not added. Try again.')}
            </Notice>
          )}
          <div className={styles.actions}>
            <Button
              type="submit"
              isDisabled={!online || !ready}
              isPending={add.isPending}
              pendingLabel="Adding…"
            >
              Add vaccine
            </Button>
          </div>
        </form>
      )}
    </>
  );
}

interface EntryDraft {
  vaccineId: string;
  dose: string;
  age: string;
}

function Schedule({ view }: { view: VaccinesView }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [entries, setEntries] = useState<EntryDraft[]>(
    view.schedule.map((e) => ({
      vaccineId: e.vaccineId,
      dose: String(e.doseNumber),
      age: String(e.dueAgeDays),
    })),
  );
  const parsed = entries.map((e) => ({
    vaccineId: e.vaccineId,
    dose: readWhole(e.dose),
    age: readWhole(e.age),
  }));
  const invalid = parsed.some(
    (e) => e.vaccineId === '' || typeof e.dose !== 'number' || typeof e.age !== 'number',
  );
  const save = useMutation({
    mutationFn: () =>
      saveImmunisationSchedule({
        entries: parsed.map((e) => ({
          vaccineId: e.vaccineId,
          doseNumber: typeof e.dose === 'number' ? e.dose : 0,
          dueAgeDays: typeof e.age === 'number' ? e.age : 0,
        })),
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(maternityKeys.vaccines, next);
      toasts.show({ title: 'Schedule saved' });
    },
  });
  const active = view.vaccines.filter((v) => v.status === 'active');
  const update = (i: number, patch: Partial<EntryDraft>) => {
    setEntries(entries.map((e, j) => (j === i ? { ...e, ...patch } : e)));
  };
  return (
    <form
      className={styles.form}
      aria-label="Immunisation schedule"
      onSubmit={(event) => {
        event.preventDefault();
        if (!invalid) save.mutate();
      }}
    >
      {entries.length === 0 ? (
        <p className={styles.meta}>
          Not set up. The due list on child health says so until you add doses.
        </p>
      ) : null}
      {entries.map((e, i) => (
        <div key={i} className={styles.when}>
          <Select
            label={`Vaccine, row ${String(i + 1)}`}
            width="md"
            options={active.map((v) => ({ id: v.id, label: v.display }))}
            value={e.vaccineId}
            onChange={(v) => {
              update(i, { vaccineId: String(v) });
            }}
          />
          <TextField
            label="Dose number"
            width="xs"
            inputMode="numeric"
            value={e.dose}
            onChange={(dose) => {
              update(i, { dose });
            }}
          />
          <TextField
            label="Due at age"
            unit="days"
            width="sm"
            inputMode="numeric"
            value={e.age}
            onChange={(age) => {
              update(i, { age });
            }}
          />
          <Button
            variant="quiet"
            dense
            onPress={() => {
              setEntries(entries.filter((_, j) => j !== i));
            }}
          >
            {`Remove row ${String(i + 1)}`}
          </Button>
        </div>
      ))}
      {save.error === null ? null : (
        <Notice tone="error">
          {errorText(save.error, 'The schedule was not saved. Try again.')}
        </Notice>
      )}
      <div className={styles.actions}>
        <Button
          isDisabled={active.length === 0}
          onPress={() => {
            setEntries([...entries, { vaccineId: active[0]?.id ?? '', dose: '1', age: '' }]);
          }}
        >
          Add a dose
        </Button>
        <Button
          type="submit"
          variant="primary"
          isDisabled={!online || invalid}
          isPending={save.isPending}
          pendingLabel="Saving…"
        >
          Save schedule
        </Button>
      </div>
    </form>
  );
}

export function MaternitySettingsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const allowed = session !== null && can(session, 'maternity.settings.manage');
  const settings = useQuery({ ...maternitySettingsQuery, enabled: allowed });
  const vaccines = useQuery({ ...vaccinesQuery, enabled: allowed });
  if (!allowed) {
    return (
      <>
        <PageHeader title="Maternity and MCH" />
        <Notice tone="info">
          You don't have permission to set up maternity and MCH. Ask an administrator if you need
          it.
        </Notice>
      </>
    );
  }
  const failed = (q: { error: unknown; refetch: () => unknown }, what: string) => (
    <Notice
      tone="error"
      action={
        <Button dense onPress={() => void q.refetch()}>
          Try again
        </Button>
      }
    >
      {errorText(q.error, `${what} couldn't be loaded. Try again.`)}
    </Notice>
  );
  return (
    <SettingsPage title="Maternity and MCH">
      <Section
        title="Danger signs, partograph and store"
        description="The clinical lead's lists and numbers. Wodi suggests none."
      >
        {settings.isPending ? <LoadingBlock label="Loading maternity settings" rows={4} /> : null}
        {settings.isError ? failed(settings, 'Maternity settings') : null}
        {settings.isSuccess ? (
          <SettingsForm key={settings.data.version} settings={settings.data} />
        ) : null}
      </Section>
      <Section title="Vaccines" description="Chosen from the catalogue's medicine stock items.">
        {vaccines.isPending ? <LoadingBlock label="Loading vaccines" rows={3} /> : null}
        {vaccines.isError ? failed(vaccines, 'Vaccines') : null}
        {vaccines.isSuccess ? <Vaccines view={vaccines.data} /> : null}
      </Section>
      <Section
        title="Immunisation schedule"
        description="Which dose is due at what age, in days. Empty until set."
      >
        {vaccines.isSuccess ? (
          <Schedule key={JSON.stringify(vaccines.data.schedule)} view={vaccines.data} />
        ) : null}
      </Section>
    </SettingsPage>
  );
}
